'use client';

import Image from 'next/image';

import { cn } from '@/shared/utils/cn';
import type { SearchComboboxItem } from './types';

interface SearchSuggestionsPreviewProps {
  activeIndex: number;
  emptyStateLabel: string;
  items: readonly SearchComboboxItem[];
  previewId: string;
  /** Prefijo opcional usado para construir el id de cada opción (aria-activedescendant). */
  optionIdPrefix?: string;
  onPreviewSelect: (item: SearchComboboxItem) => void;
}

interface SearchSuggestionsPreviewOptionProps {
  isActive: boolean;
  item: SearchComboboxItem;
  optionId?: string;
  onPreviewSelect: (item: SearchComboboxItem) => void;
}

const SearchSuggestionsPreviewOption = ({
  isActive,
  item,
  optionId,
  onPreviewSelect,
}: SearchSuggestionsPreviewOptionProps) => {
  const handleSelect = () => {
    onPreviewSelect(item);
  };

  return (
    <li aria-selected={isActive} id={optionId} role="option">
      <button
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-100',
          isActive ? 'bg-adipa-surface-lavender' : 'hover:bg-adipa-surface-lavender/60',
        )}
        type="button"
        onClick={handleSelect}
      >
        {item.imageSrc ? (
          <div className="rounded-adipa-md bg-adipa-surface-muted relative h-10 w-10 shrink-0 overflow-hidden">
            <Image fill alt="" className="object-cover" sizes="40px" src={item.imageSrc} />
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <p className="text-adipa-text-primary truncate text-sm font-semibold">{item.title}</p>
          {item.subtitle ? (
            <p className="text-adipa-text-secondary truncate text-xs opacity-80">{item.subtitle}</p>
          ) : null}
        </div>

        {item.meta ? (
          <p className="text-adipa-primary shrink-0 text-xs font-semibold">{item.meta}</p>
        ) : null}
      </button>
    </li>
  );
};

export const SearchSuggestionsPreview = ({
  activeIndex,
  emptyStateLabel,
  items,
  previewId,
  optionIdPrefix,
  onPreviewSelect,
}: SearchSuggestionsPreviewProps) => (
  <div
    className="animate-dropdown-in border-adipa-border bg-adipa-surface absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border shadow-lg"
    id={previewId}
    role="listbox"
  >
    {items.length > 0 ? (
      <ul>
        {items.map((item, index) => (
          <SearchSuggestionsPreviewOption
            isActive={index === activeIndex}
            item={item}
            key={item.id}
            optionId={optionIdPrefix ? `${optionIdPrefix}-${index}` : undefined}
            onPreviewSelect={onPreviewSelect}
          />
        ))}
      </ul>
    ) : (
      <div className="flex min-h-20 items-center justify-center px-4 py-5 text-center">
        <p className="text-adipa-text-secondary text-sm">{emptyStateLabel}</p>
      </div>
    )}
  </div>
);

export default SearchSuggestionsPreview;
